import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button, Modal, Form, Input, DatePicker, TimePicker,
  Select, Popconfirm, message, Empty, Spin, Calendar, Tag,
  Upload, Alert, List, Typography,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CalendarOutlined,
  UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  InboxOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import { useBulletinEventService } from "../../services";
import "../../styles/bulletinAdmin/components/eventsAdvisories.scss";

const { Dragger } = Upload;
const { Text } = Typography;

const TIME_FORMAT     = "h:mm A";
const API_TIME_FORMAT = "HH:mm";

function formatDateRange(startStr, endStr) {
  const start = dayjs(startStr);
  const end   = endStr ? dayjs(endStr) : null;

  // No end date, or same day → single date
  if (!end || start.isSame(end, "day")) {
    return start.format("D MMMM");
  }
  // Same month → "6 - 15 August"
  if (start.isSame(end, "month")) {
    return `${start.format("D")} - ${end.format("D MMMM")}`;
  }
  // Different months → "6 August - 15 September"
  return `${start.format("D MMMM")} - ${end.format("D MMMM")}`;
}

function renderEventLabel(event) {
  const dateLabel = formatDateRange(event.event_date, event.event_end_date);

  let timePart = "";
  if (event.event_time) {
    const start = dayjs(event.event_time, "HH:mm:ss").format(TIME_FORMAT);
    const end   = event.event_end_time
      ? ` - ${dayjs(event.event_end_time, "HH:mm:ss").format(TIME_FORMAT)}`
      : "";
    timePart = ` | ${start}${end}`;
  }

  return (
    <>
      <span className="ea-label-date">{dateLabel}{timePart}</span>
      <span className="ea-label-title">: {event.title}</span>
    </>
  );
}

export default function EventsAdvisoriesPanel({ bulletinId, isAdmin }) {
  const { service } = useBulletinEventService();

  const [events,          setEvents]          = useState([]);
  const [advisories,      setAdvisories]       = useState([]);
  const [loading,         setLoading]          = useState(false);

  // Add / Edit modal
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [form] = Form.useForm();
  const startTime = Form.useWatch("event_time", form);

  // Calendar modal
  const [showCalendar,     setShowCalendar]     = useState(false);
  const [allItems,         setAllItems]         = useState([]);
  const [calendarLoading,  setCalendarLoading]  = useState(false);
  const [selectedDate,     setSelectedDate]     = useState(null);

  // Import modal
  const [showImport,      setShowImport]      = useState(false);
  const [importFile,      setImportFile]      = useState(null);
  const [importing,       setImporting]       = useState(false);
  const [importResult,    setImportResult]    = useState(null); // { imported, failed, errors, message }

  // Export modal
  const [showExport,  setShowExport]  = useState(false);
  const [exportRange, setExportRange] = useState(null);
  const [exporting,   setExporting]   = useState(false);

  // ── Load top-10 per category ──────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!bulletinId) return;
    setLoading(true);
    try {
      const res = await service.getEvents(bulletinId);
      setEvents(res?.data?.events          || []);
      setAdvisories(res?.data?.advisories        || []);
    } catch (e) {
      console.error("Failed to load events:", e);
    } finally {
      setLoading(false);
    }
  }, [bulletinId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // ── Add / Edit handlers ───────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setShowModal(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    const start = dayjs(event.event_date);
    const end   = event.event_end_date ? dayjs(event.event_end_date) : start;
    form.setFieldsValue({
      title:            event.title,
      event_date_range: [start, end],
      event_time:       event.event_time     ? dayjs(event.event_time,     "HH:mm:ss") : null,
      event_end_time:   event.event_end_time ? dayjs(event.event_end_time, "HH:mm:ss") : null,
      category:         event.category,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const [startDate, endDate] = values.event_date_range;
      const startStr = startDate.format("YYYY-MM-DD");
      const endStr   = endDate.format("YYYY-MM-DD");

      const startTime = values.event_time ? values.event_time.format(API_TIME_FORMAT) : null;

      const payload = {
        title:          values.title.trim(),
        event_date:     startStr,
        event_end_date: startStr === endStr ? null : endStr,
        event_time:     startTime,
        // Only send end time when a start time exists
        event_end_time: (startTime && values.event_end_time)
          ? values.event_end_time.format(API_TIME_FORMAT)
          : null,
        category:       values.category,
      };
      if (editing) {
        await service.updateEvent(bulletinId, editing.id, payload);
        message.success("Entry updated.");
      } else {
        await service.createEvent(bulletinId, payload);
        message.success("Entry added.");
      }
      setShowModal(false);
      await load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error("Failed to save. Please try again.");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // ── Import handlers ───────────────────────────────────────────────────────
  const openImport = () => {
    setImportFile(null);
    setImportResult(null);
    setShowImport(true);
  };

  const handleImport = async () => {
    if (!importFile) {
      message.warning("Please select a file first.");
      return;
    }
    setImporting(true);
    try {
      const res = await service.importEvents(bulletinId, importFile);
      const data = res?.data ?? res;
      setImportResult(data);
      if (data?.imported > 0) {
        await load(); // refresh the panel lists
      }
    } catch (e) {
      const errMsg = e?.response?.data?.message || "Import failed. Please try again.";
      message.error(errMsg);
      console.error(e);
    } finally {
      setImporting(false);
    }
  };

  const handleImportClose = () => {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
  };

  // ── Export handlers ───────────────────────────────────────────────────────
  const openExport = () => {
    setExportRange(null);
    setShowExport(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportRange) {
        params.start_date = exportRange[0].format("YYYY-MM-DD");
        params.end_date   = exportRange[1].format("YYYY-MM-DD");
      }

      const token = localStorage.getItem("api_token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ebulletin/bulletins/${bulletinId}/events/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
          responseType: "blob",
        }
      );

      const rangeLabel = exportRange
        ? `${params.start_date}_to_${params.end_date}`
        : "all";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bulletin_${bulletinId}_events_${rangeLabel}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowExport(false);
    } catch (e) {
      message.error("Export failed. Please try again.");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await service.deleteEvent(bulletinId, id);
      message.success("Entry deleted.");
      await load();
    } catch (e) {
      message.error("Failed to delete.");
    }
  };

  // ── Calendar edit / delete (acts on the full-list modal) ─────────────────
  const handleCalendarEdit = (item) => {
    setShowCalendar(false);   // close calendar first
    openEdit(item);           // then open the edit form
  };

  const handleCalendarDelete = async (id) => {
    try {
      await service.deleteEvent(bulletinId, id);
      message.success("Entry deleted.");
      // Refresh the full list shown in the calendar
      const res = await service.getAllEvents(bulletinId);
      setAllItems(res?.data || []);
      // Also keep the top-10 panels in sync
      await load();
    } catch (e) {
      message.error("Failed to delete.");
    }
  };

  // ── Calendar handlers ─────────────────────────────────────────────────────
  const openCalendar = async () => {
    setShowCalendar(true);
    setSelectedDate(null);
    setCalendarLoading(true);
    try {
      const res = await service.getAllEvents(bulletinId);
      setAllItems(res?.data || []);
    } catch (e) {
      console.error("Failed to load calendar data:", e);
    } finally {
      setCalendarLoading(false);
    }
  };

  const itemsByDate = useMemo(() => {
    const map = {};
    allItems.forEach((item) => {
      const start = dayjs(item.event_date);
      const end   = item.event_end_date ? dayjs(item.event_end_date) : start;
      // Mark every day in the range so the calendar dot appears across the span
      let cur = start;
      while (cur.isBefore(end, "day") || cur.isSame(end, "day")) {
        const key = cur.format("YYYY-MM-DD");
        if (!map[key]) map[key] = [];
        if (!map[key].find((i) => i.id === item.id)) map[key].push(item);
        cur = cur.add(1, "day");
      }
    });
    return map;
  }, [allItems]);

  const dateCellRender = (date) => {
    const key   = date.format("YYYY-MM-DD");
    const items = itemsByDate[key] || [];
    if (!items.length) return null;
    const hasEvents     = items.some((i) => i.category === "events");
    const hasAdvisories = items.some((i) => i.category === "advisories");
    return (
      <div className="cal-dots">
        {hasEvents     && <span className="cal-dot cal-dot--events"     />}
        {hasAdvisories && <span className="cal-dot cal-dot--advisories" />}
      </div>
    );
  };

  const selectedDateItems = useMemo(() => {
    if (!selectedDate) return [];
    return itemsByDate[selectedDate.format("YYYY-MM-DD")] || [];
  }, [selectedDate, itemsByDate]);

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderList = (items) => {
    if (!items.length) return null;
    return (
      <ul className="ea-list">
        {items.map((item) => (
          <li key={item.id} className="ea-item">
            <span className="ea-label">
              {renderEventLabel(item)}
              {isAdmin && (
                <span className="ea-actions">
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(item)} />
                  <Popconfirm
                    title="Delete this entry?"
                    onConfirm={() => handleDelete(item.id)}
                    okText="Delete" okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="ea-panel">
      <div className="ea-admin-bar">
        <Button
          icon={<DownloadOutlined />}
          onClick={openExport}
          className="ea-export-btn"
        >
          Export
        </Button>
        {isAdmin && (
          <>
            <Button
              icon={<UploadOutlined />}
              onClick={openImport}
              className="ea-import-btn"
            >
              Import CSV / XLSX
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} className="ea-add-btn">
              Add Entry
            </Button>
          </>
        )}
      </div>

      <Spin spinning={loading}>
        {/* EVENTS section */}
        <div className="ea-section">
          <div className="ea-section-header">
            <span className="ea-section-title">Events</span>
            <button className="ea-header-calendar-btn" onClick={openCalendar} title="View calendar">
              <CalendarOutlined />
            </button>
          </div>
          <div className="ea-section-body">
            {events.length === 0
              ? (
                <Empty
                  description={isAdmin ? "No events yet." : "No upcoming events yet."}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
              : renderList(events)}
          </div>
        </div>

        {/* ADVISORIES section */}
        <div className="ea-section">
          <div className="ea-section-header">
            <span className="ea-section-title">Advisories</span>
            <button className="ea-header-calendar-btn" onClick={openCalendar} title="View calendar">
              <CalendarOutlined />
            </button>
          </div>
          <div className="ea-section-body">
            {advisories.length === 0
              ? (
                <Empty
                  description={isAdmin ? "No advisories yet." : "No upcoming advisories yet."}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
              : renderList(advisories)}
          </div>
        </div>
      </Spin>

      {/* ── Add / Edit modal ─────────────────────────────────────────────── */}
      <Modal
        title={editing ? "Edit Entry" : "Add Event / Advisory"}
        open={showModal}
        onOk={handleSave}
        onCancel={() => setShowModal(false)}
        okText={editing ? "Save Changes" : "Add"}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
            <Input placeholder="e.g. OUgnayan Wednesday" maxLength={255} showCount />
          </Form.Item>
          <Form.Item
            name="event_date_range"
            label="Date (select the same day twice for a single-day event)"
            rules={[{ required: true, message: "Date is required" }]}
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format="MMMM D, YYYY"
              allowSameDay
            />
          </Form.Item>
          <Form.Item name="event_time" label="Start Time (optional)">
            <TimePicker
              style={{ width: "100%" }}
              format="h:mm A"
              use12Hours
              onChange={(val) => {
                // Clear end time if start time is removed
                if (!val) form.setFieldValue("event_end_time", null);
              }}
            />
          </Form.Item>

          {startTime && (
            <Form.Item name="event_end_time" label="End Time (optional)">
              <TimePicker style={{ width: "100%" }} format="h:mm A" use12Hours />
            </Form.Item>
          )}
          <Form.Item name="category" label="Category" rules={[{ required: true, message: "Category is required" }]}>
            <Select placeholder="Select a category">
              <Select.Option value="events">Events</Select.Option>
              <Select.Option value="advisories">Advisories</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Import modal ────────────────────────────────────────────────── */}
      <Modal
        title="Import Events / Advisories"
        open={showImport}
        onCancel={handleImportClose}
        footer={
          importResult ? (
            <Button type="primary" onClick={handleImportClose}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleImportClose}>Cancel</Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                onClick={handleImport}
                loading={importing}
                disabled={!importFile}
                className="ea-import-submit-btn"
              >
                Import
              </Button>
            </>
          )
        }
        width={580}
        destroyOnClose
      >
        {!importResult ? (
          <>
            {/* ── Download sample link ─────────────────────────────────── */}
            <div className="ea-import-sample-row">
              <Text type="secondary" style={{ fontSize: 13 }}>
                Not sure about the format?&nbsp;
              </Text>
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                href="/samples/events-advisories-sample.csv"
                download="events-advisories-sample.csv"
                style={{ padding: 0, fontSize: 13 }}
              >
                Download sample CSV
              </Button>
            </div>

            {/* ── Column guide ─────────────────────────────────────────── */}
            <div className="ea-import-guide">
              <Text strong style={{ fontSize: 13 }}>Required columns:</Text>
              <ul className="ea-import-guide-list">
                <li><Text code>Title</Text></li>
                <li><Text code>Start Date</Text> — format: <Text italic>Month DD, YYYY</Text> (e.g. May 28, 2026)</li>
                <li><Text code>End Date</Text> — optional; same format as Start Date</li>
                <li><Text code>Start Time</Text> — optional; format: <Text italic>HH:MM AM/PM</Text> (e.g. 09:00 AM)</li>
                <li><Text code>End Time</Text> — optional; same format as Start Time</li>
                <li><Text code>Category</Text> — must be <Text strong>Events</Text> or <Text strong>Advisories</Text></li>
              </ul>
            </div>

            {/* ── File drop zone ───────────────────────────────────────── */}
            <Dragger
              accept=".csv,.xlsx,.xls"
              beforeUpload={(file) => {
                setImportFile(file);
                return false; // prevent automatic upload
              }}
              onRemove={() => setImportFile(null)}
              maxCount={1}
              className="ea-import-dragger"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: "#862633" }} />
              </p>
              <p className="ant-upload-text">
                Click or drag a <strong>.csv</strong> or <strong>.xlsx</strong> file here
              </p>
              <p className="ant-upload-hint">Max file size: 5 MB</p>
            </Dragger>
          </>
        ) : (
          /* ── Results view ──────────────────────────────────────────────── */
          <div className="ea-import-results">
            {/* Summary */}
            <div className="ea-import-summary">
              {importResult.imported > 0 && (
                <Alert
                  type="success"
                  icon={<CheckCircleOutlined />}
                  message={`${importResult.imported} ${importResult.imported === 1 ? "entry" : "entries"} imported successfully.`}
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              {importResult.failed > 0 && (
                <Alert
                  type="warning"
                  icon={<CloseCircleOutlined />}
                  message={`${importResult.failed} ${importResult.failed === 1 ? "row" : "rows"} could not be imported.`}
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              {importResult.skipped > 0 && (
                <Alert
                  type="info"
                  icon={<InfoCircleOutlined />}
                  message={`${importResult.skipped} duplicate ${importResult.skipped === 1 ? "entry" : "entries"} skipped (same Title, Start Date, End Date, and Category already exist).`}
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              )}
              {importResult.imported === 0 && importResult.failed === 0 && !importResult.skipped && (
                <Alert
                  type="info"
                  message="No data rows were found in the file."
                  showIcon
                />
              )}
            </div>

            {/* Row errors */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div className="ea-import-errors">
                <Text strong style={{ fontSize: 13 }}>Row errors:</Text>
                <List
                  size="small"
                  dataSource={importResult.errors}
                  style={{ marginTop: 8, maxHeight: 260, overflowY: "auto" }}
                  renderItem={(err) => (
                    <List.Item className="ea-import-error-item">
                      <div>
                        <Text strong style={{ fontSize: 13 }}>
                          Row {err.row}
                          {err.title && err.title !== "(no title)"
                            ? ` — "${err.title}"`
                            : ""}
                          :
                        </Text>
                        <ul className="ea-import-error-list">
                          {err.errors.map((e, i) => (
                            <li key={i}>
                              <Text type="danger" style={{ fontSize: 12 }}>{e}</Text>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Export modal ─────────────────────────────────────────────────── */}
      <Modal
        title="Export Events / Advisories"
        open={showExport}
        onCancel={() => setShowExport(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowExport(false)}>
            Cancel
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
          >
            Export
          </Button>,
        ]}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 13, display: "block", marginBottom: 12 }}>
          Optionally pick a date range to only export entries within it. Leave it blank to export everything.
        </Text>
        <DatePicker.RangePicker
          style={{ width: "100%" }}
          format="MMMM D, YYYY"
          value={exportRange}
          onChange={setExportRange}
          allowClear
        />
      </Modal>

      {/* ── Calendar modal ───────────────────────────────────────────────── */}
      <Modal
        title="Events & Advisories Calendar"
        open={showCalendar}
        onCancel={() => { setShowCalendar(false); setSelectedDate(null); }}
        footer={null}
        width={860}
        destroyOnClose
      >
        <Spin spinning={calendarLoading}>
          {/* Legend */}
          <div className="cal-legend">
            <span className="cal-legend-item">
              <span className="cal-dot cal-dot--events" /> Events
            </span>
            <span className="cal-legend-item">
              <span className="cal-dot cal-dot--advisories" /> Advisories
            </span>
          </div>

          <div className="cal-compact">
            <Calendar dateCellRender={dateCellRender} onSelect={setSelectedDate} />
          </div>

          {/* Selected-date panel */}
          {selectedDate && (
            <div className="cal-selected-panel">
              <h4>{selectedDate.format("MMMM D, YYYY")}</h4>
              {selectedDateItems.length === 0 ? (
                <Empty description="No entries on this date." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <ul className="cal-event-list">
                  {selectedDateItems.map((item) => (
                    <li key={item.id} className="cal-event-item">
                      <Tag color={item.category === "events" ? "red" : "green"}>
                        {item.category === "events" ? "Event" : "Advisory"}
                      </Tag>
                      <span className="cal-event-label">
                        {renderEventLabel(item)}
                        {isAdmin && (
                          <span className="cal-event-actions">
                            <Button
                              type="text" size="small" icon={<EditOutlined />}
                              onClick={() => handleCalendarEdit(item)}
                            />
                            <Popconfirm
                              title="Delete this entry?"
                              onConfirm={() => handleCalendarDelete(item.id)}
                              okText="Delete" okButtonProps={{ danger: true }}
                              cancelText="Cancel"
                            >
                              <Button type="text" size="small" icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
}
